import { claimCardViewModel, type ClaimCardViewModel } from './presentation.js';
import type { GeneratedRelationshipReport, RelationshipClaim } from './relationshipInterpretation.js';
import type { DimensionResult } from './rules.js';
import type { ChronologyResolution } from './chronology.js';
import { chronologyResolutionViewModel, type ChronologyResolutionViewModel } from './calculationContextPresentation.js';
import type { RelationshipContextBundle } from './relationshipContext.js';
import { buildRelationshipReportExplanationPackets, type RelationshipClaimExplanationPacket } from './relationshipExplainability.js';
import { STAR_REGISTRY } from './starRegistry.js';

export type RelationshipDimensionStatus = 'supportive' | 'mixed' | 'counter' | 'insufficient';

export interface RelationshipDimensionViewModel {
  dimension: string;
  label: string;
  status: RelationshipDimensionStatus;
  statusLabel: '支持條件' | '支持與反向並存' | '有反向條件' | '依據不足';
  supportingRuleCount: number;
  counterRuleCount: number;
  everydayExpression: string;
  strengthExpression: string;
  pressureExpression: string;
  scenarioExamples: readonly string[];
  practicalApplication: string;
  disclosure: string;
}

export interface RelationshipPersonStructureViewModel {
  roleLabel: 'A 方' | 'B 方';
  focusPalaceLabel: string;
  focusStars: readonly string[];
  surrounding: readonly string[];
  transformations: readonly string[];
}

export interface RelationshipStructuralComparisonViewModel {
  headline: string;
  lead: string;
  personA: RelationshipPersonStructureViewModel;
  personB: RelationshipPersonStructureViewModel;
  sharedFocusStars: readonly string[];
  readingGuide: string;
  disclosure: string;
}

export interface RelationshipVersionsViewModel {
  personA: { calculationRuleset: string; chronology?: ChronologyResolutionViewModel };
  personB: { calculationRuleset: string; chronology?: ChronologyResolutionViewModel };
  relationshipRulePack: string;
  templatePack: string;
  disclosure: string;
}

const MUTAGEN_LABELS: Readonly<Record<string,string>> = Object.freeze({ lu:'祿', quan:'權', ke:'科', ji:'忌' });
function relationshipStarLabel(starId:string):string { return STAR_REGISTRY.find((entry)=>entry.id===starId)?.zhTW ?? starId; }

function relationshipPersonStructure(roleLabel: 'A 方'|'B 方', person: RelationshipContextBundle['personA']): RelationshipPersonStructureViewModel {
  const surrounding=person.relatedPalaces.map((item)=>`${item.relationType==='trine'?'三方':'對宮'} ${item.palaceLabel}：${item.starLabels.join('、')||'無核心星曜'}`);
  const transformations=person.focusTransformations.map((item)=>`${item.direction==='outgoing'?'本宮飛出':'飛入本宮'} 化${MUTAGEN_LABELS[item.mutagen]??item.mutagen} · ${relationshipStarLabel(item.starId)}`);
  return Object.freeze({ roleLabel, focusPalaceLabel:person.focusPalaceLabel, focusStars:Object.freeze([...person.starLabels]), surrounding:Object.freeze(surrounding), transformations:Object.freeze(transformations) });
}

function relationshipStructuralComparison(context: RelationshipContextBundle): RelationshipStructuralComparisonViewModel {
  const shared=context.personA.focusStars.filter((a)=>context.personB.focusStars.some((b)=>b.starId===a.starId)).map((item)=>item.starLabel);
  return Object.freeze({
    headline:`${context.mapping.directionLabel}的盤面比較`,
    lead:'先分開看雙方在此關係類型中的焦點宮位，再比較同宮星曜、三方四正與四化。這一層只整理客觀盤面，不把「相同」直接解讀成相合，也不把「不同」解讀成衝突。',
    personA:relationshipPersonStructure('A 方',context.personA),
    personB:relationshipPersonStructure('B 方',context.personB),
    sharedFocusStars:Object.freeze(shared),
    readingGuide:shared.length?`雙方焦點宮目前有 ${shared.join('、')} 等共同星曜；共同星曜只代表可比較的結構交集，真正的協作或阻力仍以已核准 Relationship Rules 是否觸發為準。`:'雙方焦點宮目前沒有相同星曜；這不代表關係不合，只表示本宮層沒有直接的星曜交集，仍需看各自三方四正、四化與正式規則。',
    disclosure:'盤面比較不輸出相合率、配對分數或關係結果；未通過來源 Gate 的組合只顯示為客觀結構，不生成命理結論。',
  });
}

export interface RelationshipReportViewModel {
  relationshipType: GeneratedRelationshipReport['relationshipType'];
  mappingLabel: string;
  interpretationStatus: GeneratedRelationshipReport['interpretationStatus'];
  claims: readonly ClaimCardViewModel[];
  claimExplanations: readonly RelationshipClaimExplanationPacket[];
  dimensions: readonly RelationshipDimensionViewModel[];
  structuralComparison?: RelationshipStructuralComparisonViewModel;
  versions: RelationshipVersionsViewModel;
  emptyState?: string;
  disclosure: string;
  decisionBoundary: string;
}


const RELATIONSHIP_DIMENSION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  partner_coordination_context: '伴侶協作／互助',
  sibling_support_context: '手足支持／依靠',
  sibling_coordination_context: '手足協調一致性',
});

const RELATIONSHIP_READER_DEPTH: Readonly<Record<string, Readonly<{everyday:string;strength:string;pressure:string;scenarios:readonly string[];practical:string}>>> = Object.freeze({
  partner_coordination_context:Object.freeze({everyday:'這一面向主要看兩個人在共同生活或共同決定裡，能不能把需求說清楚、接住對方的任務，並在意見不同時留下協商空間。',strength:'條件較順時，分工、回應與補位比較容易形成穩定節奏；遇到需要一起處理的事情，不必每次都重新確認彼此要做什麼。',pressure:'有反向條件時，問題通常不是單純「合不合」，而是責任邊界、回應速度或處理方式容易互相錯位；小事累積後才會覺得彼此難配合。',scenarios:Object.freeze(['一起安排旅行、家務或共同支出時，能否先講清楚誰負責什麼。','一方臨時忙不過來時，另一方是否知道怎麼補位，而不是等到事情失控才介入。','意見不同時，能否先處理事情本身，再討論誰對誰錯。']),practical:'把重要共同事項改成可見的分工與確認點：先定責任人、完成標準與需要互相通知的時點。這比用「默契應該自然形成」來測試關係更有用。'}),
  sibling_support_context:Object.freeze({everyday:'這一面向看的是手足之間在真正需要幫忙時，支援能不能落到具體行動，而不是只看平常感情熱不熱絡。',strength:'條件較順時，遇到家庭任務、照顧安排或臨時狀況，比較容易找到可以實際承接事情的人。',pressure:'條件不順時，即使彼此有關心，也可能卡在不知道怎麼開口、誰該先處理，或支援停在口頭上。',scenarios:Object.freeze(['家裡臨時需要有人跑一趟、處理文件或陪同時，是否有人能實際接手。','需要借用資訊、人脈或時間時，彼此能不能直接說明需求與可幫到的範圍。','長期家庭責任出現時，支援是否能從一次幫忙變成可持續的安排。']),practical:'需要支援時，把需求拆成「事情、期限、需要多少時間、誰能接哪一段」，避免只用「你應該知道我要什麼」來判斷手足是否可靠。'}),
  sibling_coordination_context:Object.freeze({everyday:'這一面向重點在手足或近身家庭共同處理事情時，資訊、分工與決定能否對齊，而不是判斷誰比較孝順或親近。',strength:'條件較順時，事情有變動後比較容易重新分配工作，也比較能維持共同決定的一致性。',pressure:'有反向條件時，常見卡點是各自以為對方會處理、訊息傳遞不同步，或每次都到最後一刻才重新協商。',scenarios:Object.freeze(['共同處理長輩、家庭活動或共有物品時，是否有一個所有人都看得到的最新決定。','臨時有人無法完成原本分工時，能否快速知道誰接手，而不是讓任務懸空。','涉及不同家庭成員意見時，能否先把已確定事項和仍待討論事項分開。']),practical:'把共同事項固定成簡單的交接方式：誰負責、目前進度、下一個決定點。對容易反覆變動的家庭任務，預先約定替代方案會比臨時救火更有效。'}),
});

export function relationshipDimensionViewModel(dimension: DimensionResult): RelationshipDimensionViewModel {
  const supportingRuleCount = dimension.supportingRuleIds.length;
  const counterRuleCount = dimension.counterRuleIds.length;
  const status: RelationshipDimensionStatus = supportingRuleCount > 0 && counterRuleCount > 0
    ? 'mixed'
    : supportingRuleCount > 0
      ? 'supportive'
      : counterRuleCount > 0
        ? 'counter'
        : 'insufficient';
  const statusLabel: RelationshipDimensionViewModel['statusLabel'] = status === 'mixed'
    ? '支持與反向並存'
    : status === 'supportive'
      ? '支持條件'
      : status === 'counter'
        ? '有反向條件'
        : '依據不足';
  const depth=RELATIONSHIP_READER_DEPTH[dimension.dimension];
  const statusLead=status==='mixed'?'這張組合盤同時有順手與需要磨合的條件。':status==='supportive'?'目前觸發的條件較一致地支持這個互動方向。':status==='counter'?'目前觸發的條件以需要調整的方向為主。':'目前正式依據仍不足。';
  return Object.freeze({
    dimension: dimension.dimension, label: RELATIONSHIP_DIMENSION_LABELS[dimension.dimension] ?? dimension.dimension, status, statusLabel, supportingRuleCount, counterRuleCount,
    everydayExpression:depth?`${statusLead}${depth.everyday}`:statusLead,
    strengthExpression:depth?.strength ?? '條件較順時，先觀察雙方能否把實際互動落到可持續的分工、回應與交接。', pressureExpression:depth?.pressure ?? '條件不合時，先找出資訊、責任或回應節奏在哪裡錯位，而不是直接把問題歸因於關係好壞。',
    scenarioExamples:Object.freeze([...(depth?.scenarios ?? [])]), practicalApplication:depth?.practical ?? '把需要一起完成的事情拆成清楚的責任、期限與確認點，再用實際互動驗證這項判讀。', disclosure:'此維度只整理已觸發規則所支持的互動方向，不是關係評分或結果預測。',
  });
}

export function relationshipClaimCardViewModel(claim: RelationshipClaim): ClaimCardViewModel {
  return claimCardViewModel(claim);
}

export function relationshipVersionsViewModel(
  report: GeneratedRelationshipReport,
  chronology?: Readonly<{ personA: ChronologyResolution; personB: ChronologyResolution }>,
): RelationshipVersionsViewModel {
  const versions = report.topics[0].versions;
  return Object.freeze({
    personA: Object.freeze({
      calculationRuleset: versions.personACalculationRuleset,
      ...(chronology ? { chronology: chronologyResolutionViewModel(chronology.personA) } : {}),
    }),
    personB: Object.freeze({
      calculationRuleset: versions.personBCalculationRuleset,
      ...(chronology ? { chronology: chronologyResolutionViewModel(chronology.personB) } : {}),
    }),
    relationshipRulePack: versions.relationshipRulePack,
    templatePack: versions.templatePack,
    disclosure: '此區只顯示雙方排盤 Ruleset、Relationship RulePack、Template Pack 與可選 Chronology Provider 版本；這些是可重現性資料，不是相合率或準確率。',
  });
}

export function relationshipReportViewModel(
  report: GeneratedRelationshipReport,
  chronology?: Readonly<{ personA: ChronologyResolution; personB: ChronologyResolution }>,
  context?: RelationshipContextBundle,
): RelationshipReportViewModel {
  const claims = report.topics[0].claims.map(relationshipClaimCardViewModel);
  return {
    relationshipType: report.relationshipType,
    mappingLabel: report.mappingLabel,
    interpretationStatus: report.interpretationStatus,
    claims,
    claimExplanations: context ? buildRelationshipReportExplanationPackets(context, report) : Object.freeze([]),
    dimensions: report.topics[0].resolved.dimensions.map(relationshipDimensionViewModel),
    ...(context ? { structuralComparison: relationshipStructuralComparison(context) } : {}),
    versions: relationshipVersionsViewModel(report, chronology),
    ...(claims.length === 0 ? {
      emptyState: report.interpretationStatus === 'research_pending'
        ? '目前此關係類型尚無通過來源與測試 Gate 的正式規則，因此只顯示客觀宮位與星曜 Evidence。'
        : '目前已核准規則沒有被這兩張命盤觸發，不以無來源文字補足。',
    } : {}),
    disclosure: report.disclosure,
    decisionBoundary: '雙人關係內容是傳統紫微斗數的規則式文化解讀，不代表科學相容度，也不應單獨用於婚姻、分手、家庭疏離或其他重大關係決策。',
  };
}
